import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDispatch } from "react-redux";

import { TranscriberRef } from "@/components/Transcriber";
import {
  LIVE_TRANSCRIPTION,
  isAssemblyAIScribe,
} from "@/constants/live-transcription";
import { useTranscription } from "@/pages/ReportStudio/ReportBuilder/store/useTranscription";
import {
  bindRecordingSession,
  discardRecording,
  markRecordingSuspended,
  releaseRecordingFor,
  submissionForSession,
} from "@/services/recordingOwnership";
import { showError } from "@/utils/notification";

import type { SpeakerHint } from "../components/SpeakerChecklist";
import type { SessionMode } from "../types/sessionMode";
import * as assemblyAIRecovery from "./assemblyAIRecovery";
import type { RecoveredSession } from "./assemblyAIRecovery";
import {
  adoptRecordingBuffer,
  appendRecordingChunk,
  beginRecordingBuffer,
  cancelRecordingBuffer,
  clearRecordingFinalizeHandler,
  discardRecordingAudio,
  finalizeRecordingBuffer,
  isRecordingBufferCancelled,
  notifyRecordingFinalized,
  resetRecordingBuffer,
  resumeRecordingBuffer,
  setRecordingFinalizeHandler,
  suspendRecordingBuffer,
} from "./recordingBuffer";
import * as persistenceV1 from "./recordingPersistence";

const {
  beginPersistedRecording,
  clearPersistedRecording,
  persistRecordingChunk,
  releasePersistedRecording,
} = isAssemblyAIScribe() ? assemblyAIRecovery : persistenceV1;

/**
 * Stops writing to a stopped session's durable copy without deleting it
 * (MDRS-551). AssemblyAI also marks it stopped first, so recovery offers Send
 * rather than Resume; the release waits for that write.
 */
const keepStoppedRecording = (sessionId: string): void => {
  if (isAssemblyAIScribe()) {
    void assemblyAIRecovery.markPersistedRecordingStopped(sessionId);
  }
  void releasePersistedRecording(sessionId);
};

// Floor for the live AssemblyAI `max_speakers` cap (Doctor + Examinee).
const MIN_LIVE_SPEAKERS = 2;

/**
 * Lifecycle status of an in-panel Scribe recording session (MDRS-116).
 *
 *  - `idle`       — no session; the consent modal is the surface.
 *  - `connecting` — Start Recording clicked, WebSocket handshake in flight.
 *  - `recording`  — live capture + transcription running.
 *  - `paused`     — capture temporarily suspended (no audio to AssemblyAI).
 *  - `stopped`    — session ended, blob assembled, transcript complete.
 */
export type RecordingSessionStatus =
  | "idle"
  | "connecting"
  | "recording"
  | "paused"
  | "stopped";

/**
 * Optional per-session settings for `RecordingSession.start` beyond
 * `speakerHints`. Bundled into one object — rather than more
 * positional params — since `caseNumber`, `mode`, and `micDeviceId` would
 * otherwise sit side by side with no type-level distinction, inviting a
 * future param to silently land in the wrong slot.
 */
export type StartSessionOptions = {
  initialNotes?: string;
  caseNumber?: string;
  mode?: SessionMode;
  /** Telehealth shared call-tab audio track — see useCallAudioShare. */
  callAudioTrack?: MediaStreamTrack;
  /** `deviceId` chosen in the Audio Setup step's mic picker. */
  micDeviceId?: string;
  /** AssemblyAI only: continue this interrupted recording (same audio, new transcript segment). */
  resumeFrom?: RecoveredSession;
};

export type UseRecordingSessionArgs = {
  transcriberRef?: React.RefObject<TranscriberRef | null>;
  /**
   * Fired at the start of a new recording, before the MediaRecorder is
   * constructed, so the owner can drop any stale blob from a prior session.
   */
  onRecordingSessionStart?: () => void;
  /**
   * Fired when the user finalises a session (Done / auto after Stop). Receives
   * the captured audio for the diarized AI summarize path (MDRS-29); null when
   * the session is cancelled/discarded. Also receives the speaker hints this
   * session was started with (MDRS-163 follow-on), snapshotted at the same
   * moment as the blob so the consumer doesn't race a later `start()` call
   * resetting the hook's own `speakerHints` state. `savedSessionId` identifies
   * the recording's saved copy, kept until the consumer confirms it was sent
   * via `clearSavedRecording` (MDRS-551); null when nothing was kept.
   */
  onRecordingSessionEnd?: (
    audioBlob: Blob | null,
    speakerHints: SpeakerHint[],
    savedSessionId: string | null
  ) => void;
  /**
   * Populated with the discard-and-teardown handler so the page's navigation
   * guard can stop the session on confirm (MDRS-85 nav guard, preserved).
   */
  stopRecordingRef?: React.MutableRefObject<(() => void) | null>;
  /**
   * Resolves the case that owns the session. Recorded on the module-level audio
   * buffer as a diagnostic tag (MDRS-233) — cross-session isolation itself is
   * enforced by session-id ownership, not by this value.
   *
   * A GETTER, not a value: the scribe page claims ownership (`beginSession`) and
   * calls `start()` in the same tick, so a rendered prop would still hold the
   * pre-claim value — always null on the first recording, and the *previous*
   * case's number afterwards. Reading it at claim time is the only correct
   * option, and it keeps `start` referentially stable.
   */
  getOwningCaseNumber?: () => string | null;
  /**
   * Resolves the examinee name for the session, read at the same claim-time
   * moment as `getOwningCaseNumber` (SCRIBE-05). Persisted alongside the audio
   * so a recovered recording — found after a hard reload wiped everything else
   * in memory, including the dock store this getter normally reads from — can
   * still be routed to the right case's summarize request without the user
   * having to re-identify it.
   */
  getOwningExamineeName?: () => string | null;
};

export type RecordingSession = {
  status: RecordingSessionStatus;
  /** True while `recording` or `paused` — the live view owns the panel. */
  isActive: boolean;
  /** True once stopped — the complete banner + transcript own the panel. */
  isStopped: boolean;
  isConnecting: boolean;
  isPaused: boolean;
  /** MediaRecorder that drives the LiveAudioVisualizer; null when not capturing. */
  mediaRecorder: MediaRecorder | null;
  /** The audio blob assembled once the session stops (MDRS-29). */
  recordedAudioBlob: Blob | null;
  /** Human-readable input device label for the "Input: …" caption. */
  inputDeviceLabel: string;
  /**
   * The checked speakers from the consent checklist, paired with their role
   * (MDRS-163 follow-on) — e.g. [{role:"Doctor",name:"Dr. Sarah Johnson"}].
   * Exposed here (not just consumed at `start`) so the session-end consumer
   * can read it once the session stops, well after `start` returned, and
   * forward it into the batch summarize payload as `speaker_hints`. Empty
   * when no session has started yet.
   */
  speakerHints: SpeakerHint[];
  /** Whether this session is recording an in-person or telehealth assessment. */
  sessionMode: SessionMode;
  /**
   * Begin a session. `speakerHints` is the checked speakers from the consent
   * checklist, paired with role — see `speakerHints` above. `options.caseNumber`
   * is forwarded to the streaming token request so the backend can enforce the
   * recording-consent gate (SCRIBE-02) for the life of the session, including
   * token refreshes. `options.mode` defaults to "in_person";
   * `options.callAudioTrack` is the telehealth shared call-tab audio track (see
   * useCallAudioShare) to mix with the mic — omit for in-person sessions.
   * `options.micDeviceId` is the Audio Setup step's chosen input device — omit
   * for the browser default.
   */
  /** Resolves true once capture is running, false if the start failed. */
  start: (
    speakerHints?: SpeakerHint[],
    options?: StartSessionOptions
  ) => Promise<boolean>;
  pause: () => void;
  resume: () => void;
  /** Stop capture; assembles the blob and moves to `stopped`. */
  stop: () => void;
  /**
   * Discard the session (no blob surfaced) and return to idle. On a stopped
   * session this also deletes its saved recording (MDRS-551 discard).
   */
  cancel: () => void;
  /**
   * Delete a stopped recording's saved copy. A normal stop keeps it (MDRS-551)
   * so a failed upload isn't lost; call this with the `savedSessionId` from
   * `onRecordingSessionEnd` once that recording's summarize request is accepted.
   * Scoped by id: an earlier recording's late upload must never clear a newer one.
   */
  clearSavedRecording: (sessionId: string) => Promise<void>;
  /** Live engine failed: stop capture, keep the durable copy for Resume, and return to idle. */
  suspend: () => void;
  /** Dismiss the completed (stopped) session view and return to idle. */
  reset: () => void;
};

/**
 * Owns the entire live-recording lifecycle for the Scribe session — the
 * MediaRecorder wiring, pause/stop/cancel handlers, blob assembly, the
 * `isModalSession` store flag, and the navigation-guard teardown ref.
 *
 * Lifted out of AudioRecorder (MDRS-116) so the in-panel ScribeSession can
 * drive the live view while the consent gate stays a modal. AudioRecorder is
 * now consent-only and calls `start()` when the user confirms consent.
 */
export const useRecordingSession = ({
  transcriberRef,
  onRecordingSessionStart,
  onRecordingSessionEnd,
  stopRecordingRef,
  getOwningCaseNumber,
  getOwningExamineeName,
}: UseRecordingSessionArgs): RecordingSession => {
  const dispatch = useDispatch();

  const [status, setStatus] = useState<RecordingSessionStatus>("idle");
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState<Blob | null>(null);
  const [inputDeviceLabel, setInputDeviceLabel] = useState("Default microphone");
  const [speakerHints, setSpeakerHints] = useState<SpeakerHint[]>([]);
  const [sessionMode, setSessionMode] = useState<SessionMode>("in_person");

  // Audio chunks live in a module-level buffer, NOT a ref (MDRS-233): a remount
  // of the owning RecordingHost used to destroy the ref and every captured chunk
  // with it. Each `start()` claims the buffer; `sessionIdRef` holds this hook
  // instance's claim so stop/cancel can finalize it even when the instance
  // didn't start the session (re-adoption after a remount, below).
  const sessionIdRef = useRef<string | null>(null);
  // The stopped session whose durable copy was kept for `clearSavedRecording`.
  const savedSessionIdRef = useRef<string | null>(null);

  /**
   * Wires the buffer-writing listeners onto a MediaRecorder. Used both by
   * `start` and by re-adoption after a remount — a re-adopted recorder's
   * original listeners died with the closure that registered them, so it must be
   * re-wired or its chunks would never reach the buffer.
   */
  const attachRecorderListeners = useCallback(
    (recorder: MediaRecorder, sessionId: string) => {
      recorder.addEventListener("dataavailable", (event) => {
        // Cancellation is read from the BUFFER, not a hook ref: this listener may
        // belong to an instance that has since unmounted, whose ref the live
        // instance cannot reach (MDRS-233 review).
        if (isRecordingBufferCancelled()) return;
        if (event.data) {
          appendRecordingChunk(sessionId, event.data);
          // Best-effort durable mirror (SCRIBE-05): the in-memory buffer above
          // cannot survive a hard reload/crash, so without this every chunk
          // captured so far is silently lost with no error and no salvage.
          void persistRecordingChunk(sessionId, event.data);
        }
      });

      recorder.addEventListener("stop", () => {
        // Assembles from the module buffer and releases it. `owned` is false for
        // a stale recorder whose `stop` landed after a newer session claimed the
        // buffer — that must not be reported as a completion, or it would end the
        // recording still in progress, and its durable copy must be left alone.
        const { owned, blob, cancelled, suspended } =
          finalizeRecordingBuffer(sessionId);
        if (!owned) return;
        if (suspended) {
          notifyRecordingFinalized(null, true);
          return;
        }
        if (cancelled) {
          // Discarded by the user — nothing left worth recovering.
          void clearPersistedRecording(sessionId);
          return;
        }
        // Keep the durable copy (MDRS-551): it's the only copy that survives a
        // failed upload or a reload, so it's dropped only once the summarize
        // request is accepted (`clearSavedRecording`) or the user discards.
        keepStoppedRecording(sessionId);
        // Report through the buffer, not directly: this listener belongs to
        // whichever instance called start(), and after a remount that instance's
        // setState is a no-op. The registered handler is always the live one.
        notifyRecordingFinalized(blob, false, sessionId);
      });

      recorder.addEventListener("error", (event) => {
        console.error("[useRecordingSession] MediaRecorder error", event);
        showError(
          "Audio capture failed mid-session. The live transcript was preserved but speaker-stamped transcripts won't be available for this recording.",
          dispatch
        );
        // A capture failure is NOT a user cancellation: reusing the cancel flag
        // would suppress finalisation entirely and strand the session in
        // `recording` forever, silently dropping the live transcript the toast
        // above promises was preserved. Discard the (unusable) audio but still
        // finalise, so status reaches `stopped` and the text-only summarize runs.
        discardRecordingAudio();
        void clearPersistedRecording(sessionId);
        notifyRecordingFinalized(null);
      });
    },
    [dispatch]
  );

  // Register THIS instance as the one that receives session completion, so the
  // blob lands on the mounted hook even when the recorder's `stop` listener
  // belongs to an instance that has since unmounted (MDRS-233).
  useEffect(() => {
    const handler = (blob: Blob | null, suspended?: boolean, sessionId?: string) => {
      if (suspended) {
        sessionIdRef.current = null;
        setMediaRecorder(null);
        // Idle only once the last chunk has landed, so the Resume prompt sees all of it.
        void assemblyAIRecovery
          .releasePersistedRecording()
          .finally(() => setStatus("idle"));
        return;
      }
      savedSessionIdRef.current = sessionId ?? null;
      if (blob) setRecordedAudioBlob(blob);
      // Flip to stopped only after the blob exists so Done can't fire with a
      // null blob (MDRS-29 review — closes the stop()/event race).
      setStatus("stopped");
    };
    setRecordingFinalizeHandler(handler);
    // Clear only if still ours: a remount registers the new instance's handler
    // before this cleanup could otherwise wipe it.
    return () => clearRecordingFinalizeHandler(handler);
  }, []);

  // Re-adopt an in-flight session on mount. A remount of the owning host creates
  // a fresh hook instance with `status: "idle"` and no MediaRecorder, so without
  // this the live recording is orphaned: its audio sits in the module buffer but
  // `stop()` has nothing to stop and finalises with a null blob — the exact
  // MDRS-233 symptom this hook is meant to prevent. Mount-only by design.
  useEffect(() => {
    const inFlight = adoptRecordingBuffer();
    if (!inFlight) return;
    sessionIdRef.current = inFlight.sessionId;
    if (inFlight.recorder) {
      // Only wire listeners if nobody else already has them on this recorder —
      // they outlive the unmounted component, so re-wiring would double-count.
      if (inFlight.needsListeners) {
        attachRecorderListeners(inFlight.recorder, inFlight.sessionId);
      }
      // Synchronous setState is the point of this effect: syncing React state to
      // an external system (the module-level recorder) that already exists at
      // mount. One extra render at mount, no cascade.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMediaRecorder(inFlight.recorder);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStatus(inFlight.status ?? "recording");
    }
    // Mount-only: re-adopting on every change would double-wire the listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark the session as modal/panel-owned for as long as it's active or
  // stopped. Section textareas read this flag and suppress live writes so the
  // streaming partials don't pollute the underlying transcript — the Done flow
  // (summarize) is the only writer.
  const setModalSession = useTranscription((s) => s.setModalSession);

  const isConnecting = status === "connecting";
  const isPaused = status === "paused";
  const isStopped = status === "stopped";
  const isActive = status === "recording" || status === "paused";

  // Keep the store flag in sync with the LIVE portion of a session (connecting /
  // recording / paused) so the section editors suppress live writes while the
  // in-panel ScribeSession owns the view. Once stopped, the panel shows the
  // persisted full-transcript viewer, so the flag clears and search/filter work.
  useEffect(() => {
    const liveSessionOwnsPanel =
      status === "connecting" || status === "recording" || status === "paused";
    setModalSession(liveSessionOwnsPanel);
    return () => setModalSession(false);
  }, [status, setModalSession]);

  const start = useCallback(
    async (
      newSpeakerHints: SpeakerHint[] = [],
      options: StartSessionOptions = {}
    ) => {
      if (!transcriberRef?.current) return false;

      const {
        caseNumber,
        mode = "in_person",
        callAudioTrack,
        micDeviceId,
      } = options;
      const resumeFrom = isAssemblyAIScribe() ? options.resumeFrom : undefined;

      // Drop any blob from a prior incomplete session so a later fallback POST
      // can't reuse stale audio from a different examinee (MDRS-29 review).
      onRecordingSessionStart?.();

      setSpeakerHints(newSpeakerHints);
      setSessionMode(mode);
      setStatus("connecting");

      const result = await transcriberRef.current.startTranscription({
        caseNumber,
        micDeviceId,
        ...(resumeFrom ? { offsetSec: resumeFrom.resumeOffsetMs / 1000 } : {}),
        ...(newSpeakerHints.length > 0
          ? { maxSpeakers: Math.max(MIN_LIVE_SPEAKERS, newSpeakerHints.length) }
          : {}),
        ...(mode === "telehealth" && callAudioTrack
          ? { extraAudioTrack: callAudioTrack }
          : {}),
      });

      if (!result?.mediaStream) {
        // Handshake failed — the Transcriber surfaces its own error. Return to
        // idle so the consent modal can be retried.
        setStatus("idle");
        return false;
      }

      // Resolve the input device label for the "Input: …" / "You" caption.
      // Read from getMicTrack(), NOT result.mediaStream's own track — in
      // telehealth sessions mediaStream is the MIXED destination stream,
      // whose track label is a browser-generated placeholder
      // ("MediaStreamAudioDestinationNode"), not the mic's name. Falls back
      // to a sensible default when the track has no label (permissions quirk).
      const micLabel = transcriberRef.current.getMicTrack()?.label;
      if (micLabel) {
        setInputDeviceLabel(micLabel);
      }

      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(
          result.mediaStream,
          isAssemblyAIScribe()
            ? {
                audioBitsPerSecond:
                  LIVE_TRANSCRIPTION.assemblyai.recorderAudioBitsPerSecond,
              }
            : undefined
        );
      } catch (err) {
        // Synchronous throw on browsers that can't negotiate a codec. Tear the
        // live transcription down and return to idle so the user can Cancel /
        // use Upload instead (mirrors the prior AudioRecorder behaviour).
        console.error("[useRecordingSession] MediaRecorder unsupported", err);
        showError(
          "Couldn't start audio capture in this browser. Try Chrome or Firefox, or use the Upload recording flow instead.",
          dispatch
        );
        if (transcriberRef.current?.isTranscribing) {
          transcriberRef.current.stopTranscription();
        }
        setStatus("idle");
        return false;
      }

      // Claim the module buffer for this session. Discards anything a prior
      // session left behind, so audio can never cross examinees (MDRS-29).
      const owningCaseNumber = getOwningCaseNumber?.() ?? null;
      const mimeType = resumeFrom?.mimeType || recorder.mimeType || "audio/webm";
      const sessionId = resumeFrom
        ? resumeRecordingBuffer({
            sessionId: resumeFrom.sessionId,
            caseNumber: owningCaseNumber,
            mimeType,
            recorder,
            priorChunks: resumeFrom.chunks,
          })
        : beginRecordingBuffer({
            // Read at claim time — the owner is set in the same tick as this call.
            caseNumber: owningCaseNumber,
            // Browser-dependent: Chrome/Firefox return `audio/webm`, Safari may
            // return `audio/mp4`. Tagged onto the assembled blob.
            mimeType,
            // Published so a hook that mounts mid-session can re-adopt and stop it.
            recorder,
          });
      const submissionId = bindRecordingSession(sessionId, resumeFrom?.submissionId);
      sessionIdRef.current = sessionId;
      savedSessionIdRef.current = null;
      setRecordedAudioBlob(null);

      if (resumeFrom) {
        // Keeps the audio and earlier transcript segments; writes a new segment.
        assemblyAIRecovery.resumePersistedRecording(resumeFrom);
      } else {
        // Best-effort durable mirror (SCRIBE-05) — see recordingPersistence.ts.
        // Fire-and-forget: it claims ownership synchronously before any await, so
        // the very first chunk write below can't race it.
        void beginPersistedRecording({
          submissionId,
          sessionId,
          caseNumber: owningCaseNumber,
          examineeName: getOwningExamineeName?.() ?? null,
          mimeType,
          speakerHints: newSpeakerHints,
          mode,
          ...(micDeviceId ? { micDeviceId } : {}),
        });
      }

      attachRecorderListeners(recorder, sessionId);

      // 1-second timeslices: bounds data loss on a crash to the last second and
      // keeps the blob-before-stopped invariant robust across browsers.
      recorder.start(1000);
      setMediaRecorder(recorder);
      setStatus("recording");
      return true;
    },
    [
      transcriberRef,
      onRecordingSessionStart,
      dispatch,
      getOwningCaseNumber,
      getOwningExamineeName,
      attachRecorderListeners,
    ]
  );

  const pause = useCallback(() => {
    if (!transcriberRef?.current) return;
    transcriberRef.current.pauseTranscription();
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.pause();
    }
    setStatus("paused");
  }, [mediaRecorder, transcriberRef]);

  const resume = useCallback(() => {
    if (!transcriberRef?.current) return;
    transcriberRef.current.resumeTranscription();
    if (mediaRecorder && mediaRecorder.state === "paused") {
      mediaRecorder.resume();
    }
    setStatus("recording");
  }, [mediaRecorder, transcriberRef]);

  const stop = useCallback(() => {
    if (transcriberRef?.current?.isTranscribing) {
      transcriberRef.current.stopTranscription();
    }
    // The `stop` listener flips status to `stopped` once the blob is
    // assembled — do NOT set it here (MDRS-29 review).
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    } else {
      // No live recorder (e.g. codec error path, or a recorder that already
      // stopped). The `stop` listener won't fire, so finalise the buffer here —
      // otherwise its chunks are never assembled AND never released, holding
      // Blob memory at module scope until the next start() (MDRS-233 review).
      const sessionId = sessionIdRef.current;
      const { owned, blob, cancelled } = sessionId
        ? finalizeRecordingBuffer(sessionId)
        : { owned: false, blob: null, cancelled: false };
      sessionIdRef.current = null;
      // Mirrors the `stop` listener's bail so neither call site can flip a
      // session it doesn't own. `!owned` means a newer session holds the buffer;
      // leave that session's audio alone and just settle this stale view.
      if (!owned || !sessionId || cancelled) {
        if (owned && sessionId) void clearPersistedRecording(sessionId);
        setStatus(cancelled ? "idle" : "stopped");
      } else {
        // Kept until the upload is accepted, same as the `stop` listener (MDRS-551).
        keepStoppedRecording(sessionId);
        notifyRecordingFinalized(blob, false, sessionId);
      }
    }
    setMediaRecorder(null);
  }, [mediaRecorder, transcriberRef]);

  const cancel = useCallback(() => {
    void discardRecording();
    // Mark cancelled on the BUFFER before stop() so queued events are no-ops for
    // every instance's listeners, not just this one's (MDRS-233 review): after a
    // remount the live recorder's listener belongs to an unmounted instance, and
    // a per-instance flag let a discarded session still dispatch a summarize.
    cancelRecordingBuffer();
    if (transcriberRef?.current?.isTranscribing) {
      transcriberRef.current.stopTranscription();
    }
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      // Leave the buffer in place: `stop` fires ASYNCHRONOUSLY, and its listener
      // needs to still see `cancelled` to suppress finalisation. It releases the
      // buffer itself via finalizeRecordingBuffer's cancelled branch, which also
      // clears the durable copy.
      mediaRecorder.stop();
    } else {
      resetRecordingBuffer();
      // Also covers a stopped session whose recording was never confirmed sent.
      const unsentSessionId = sessionIdRef.current ?? savedSessionIdRef.current;
      if (unsentSessionId) void clearPersistedRecording(unsentSessionId);
    }
    sessionIdRef.current = null;
    savedSessionIdRef.current = null;
    setRecordedAudioBlob(null);
    setMediaRecorder(null);
    setStatus("idle");
  }, [mediaRecorder, transcriberRef]);

  const clearSavedRecording = useCallback(async (sessionId: string) => {
    if (savedSessionIdRef.current === sessionId) savedSessionIdRef.current = null;
    const submissionId = submissionForSession(sessionId);
    await clearPersistedRecording(sessionId);
    releaseRecordingFor(submissionId);
  }, []);

  const suspend = useCallback(() => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    markRecordingSuspended();
    suspendRecordingBuffer();
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      // The `stop` listener sees the suspended buffer after the final chunk is flushed.
      mediaRecorder.stop();
    } else if (finalizeRecordingBuffer(sessionId).owned) {
      notifyRecordingFinalized(null, true);
    }
    showError(
      "Live transcription stopped unexpectedly. Your recording was saved — open the case to resume it.",
      dispatch
    );
  }, [mediaRecorder, dispatch]);

  // Once the session stops, surface the captured audio to the AI summarize path
  // (MDRS-29). There's no explicit "Done" in the in-panel design (MDRS-116) —
  // stopping is the finalisation — so this fires automatically on the
  // recording → stopped transition, exactly once per stop.
  //
  // Fire even when the blob is null: a session can stop with no audio chunks
  // (the `dataavailable`-less / codec-error path) yet still have a live
  // transcript, and the consumer's session-end effect runs a text-only,
  // diarization-less summarize in that case. Guarding on blob identity would
  // skip the null→null case and silently drop that fallback (regression from
  // the old unconditional Done handler). So we guard on the stop transition,
  // not the blob value.
  const finalizedForStopRef = useRef(false);
  useEffect(() => {
    if (status !== "stopped") {
      finalizedForStopRef.current = false;
      return;
    }
    if (finalizedForStopRef.current) return;
    finalizedForStopRef.current = true;
    onRecordingSessionEnd?.(
      recordedAudioBlob,
      speakerHints,
      savedSessionIdRef.current
    );
  }, [status, recordedAudioBlob, speakerHints, onRecordingSessionEnd]);

  const reset = useCallback(() => {
    setRecordedAudioBlob(null);
    setStatus("idle");
  }, []);

  // Expose the discard-and-teardown handler while a session is live so the
  // page's navigation guard can stop it on confirm (MDRS-85). Not once stopped:
  // cancelling then deletes a recording that may not have been sent (MDRS-551).
  useEffect(() => {
    if (!stopRecordingRef) return;
    stopRecordingRef.current = isActive ? cancel : null;
    return () => {
      stopRecordingRef.current = null;
    };
  }, [stopRecordingRef, isActive, cancel]);

  return useMemo(
    () => ({
      status,
      isActive,
      isStopped,
      isConnecting,
      isPaused,
      mediaRecorder,
      recordedAudioBlob,
      inputDeviceLabel,
      speakerHints,
      sessionMode,
      start,
      pause,
      resume,
      stop,
      cancel,
      clearSavedRecording,
      suspend,
      reset,
    }),
    [
      status,
      isActive,
      isStopped,
      isConnecting,
      isPaused,
      mediaRecorder,
      recordedAudioBlob,
      inputDeviceLabel,
      speakerHints,
      sessionMode,
      start,
      pause,
      resume,
      stop,
      cancel,
      clearSavedRecording,
      suspend,
      reset,
    ]
  );
};
